/**
 * Timed crystal effects: summing the modifiers of every effect active on a
 * character, and what those sums do to combat, healing and praxis. The host
 * owns the effects themselves (see crystal-effect-service.ts in
 * @sorrem/crucible-host).
 */

import type { CrystalEffectBehaviors, CrystalEffectModifiers, CrystalExplosion, CrystalPulse } from './types/crystals';
import type { CombatModifierState } from './types/game';

const MODIFIER_KEYS: ReadonlyArray<keyof CrystalEffectModifiers> = [
    'outgoingDamage',
    'damageTaken',
    'defense',
    'healingReceived',
    'praxisGain',
];

/** The sum of several effects' modifiers; a modifier none of them set is left out. */
export function resolveCrystalEffectModifiers(modifiers: ReadonlyArray<CrystalEffectModifiers>): CrystalEffectModifiers {
    const total: { -readonly [Key in keyof CrystalEffectModifiers]: number } = {};
    for (const entry of modifiers) {
        for (const key of MODIFIER_KEYS) {
            const value = entry[key];
            if (value !== undefined) {
                total[key] = (total[key] ?? 0) + value;
            }
        }
    }
    return total;
}

/** The combat modifiers a character's summed crystal effects give. */
export function toCombatModifiers(modifiers: CrystalEffectModifiers): CombatModifierState {
    return {
        ...(modifiers.outgoingDamage !== undefined ? { damageMultiplier: modifiers.outgoingDamage } : {}),
        ...(modifiers.damageTaken !== undefined ? { damageTakenMultiplier: modifiers.damageTaken } : {}),
        ...(modifiers.defense !== undefined ? { avoidanceMultiplier: modifiers.defense } : {}),
    };
}

/** Essence a heal of `amount` restores after the target's healing received modifier; at least 1 for a positive heal. */
export function resolveHealingReceived(amount: number, modifiers: CrystalEffectModifiers | undefined): number {
    if (amount <= 0) {
        return 0;
    }
    return Math.max(1, Math.round(amount * Math.max(0, 1 + (modifiers?.healingReceived ?? 0))));
}

/**
 * Chance of a landed weapon hit striking a weak point when a crystal only
 * detects them (Insight and Revelation Splinters), and when it makes them
 * always visible (their Fragments and Completes). First-pass numbers.
 */
export const WEAK_POINT_DETECTED_CHANCE = 0.2;
export const WEAK_POINT_VISIBLE_CHANCE = 0.35;
/** How far an Insight Fragment reveals the map around its user, in tiles. First-pass number. */
export const INSIGHT_FRAGMENT_REVEAL_RADIUS_TILES = 40;
/** A weak-point hit deals this many times its damage, after every other damage bonus. */
export const WEAK_POINT_DAMAGE_MULTIPLIER = 1.5;

/**
 * Damage a landed hit deals after the target's damage taken modifier. A hit
 * that dealt damage still deals at least 1; a full block (0) stays 0.
 */
export function resolveDamageTaken(damage: number, modifiers: CombatModifierState | undefined): number {
    if (damage <= 0) {
        return 0;
    }
    return Math.max(1, Math.round(damage * Math.max(0, 1 + (modifiers?.damageTakenMultiplier ?? 0))));
}

function strongerExplosion<T extends CrystalExplosion>(current: T | undefined, next: T | undefined): T | undefined {
    if (!next) {
        return current;
    }
    return !current || next.damageFraction > current.damageFraction ? next : current;
}

/**
 * The behaviors of several active effects together: for each behavior, the
 * strongest one (the bigger share of damage, the wider sight). Unlike
 * modifiers, behaviors don't add up: two explosions on one hit would be one
 * explosion's job, and two crystals revealing weak points reveal them once.
 */
export function resolveCrystalEffectBehaviors(behaviors: ReadonlyArray<CrystalEffectBehaviors>): CrystalEffectBehaviors {
    let explodeOnHit: CrystalExplosion | undefined;
    let pulse: CrystalPulse | undefined;
    let drawAggression: CrystalEffectBehaviors['drawAggression'];
    let revealWeakPoints: CrystalEffectBehaviors['revealWeakPoints'];
    for (const entry of behaviors) {
        explodeOnHit = strongerExplosion(explodeOnHit, entry.explodeOnHit);
        pulse = strongerExplosion(pulse, entry.pulse);
        if (entry.drawAggression && entry.drawAggression.sightRangeMultiplier > (drawAggression?.sightRangeMultiplier ?? 0)) {
            drawAggression = entry.drawAggression;
        }
        if (entry.revealWeakPoints && entry.revealWeakPoints.chance > (revealWeakPoints?.chance ?? 0)) {
            revealWeakPoints = entry.revealWeakPoints;
        }
    }
    return {
        ...(explodeOnHit ? { explodeOnHit } : {}),
        ...(pulse ? { pulse } : {}),
        ...(drawAggression ? { drawAggression } : {}),
        ...(revealWeakPoints ? { revealWeakPoints } : {}),
    };
}
